import { useEffect, useMemo, useState } from "react";
import { Search, Download, LogOut, RefreshCw, X, BarChart3, Bell, CheckCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["New", "Contacted", "In Discussion", "Proposal Sent", "Converted", "Not Interested"];

const fmtDate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return d;
    }
};

function Stat({ label, value, testid }) {
    return (
        <div className="bg-cream-soft border border-burgundy/10 rounded-2xl p-6 card-lift" data-testid={testid}>
            <div className="font-serif text-4xl text-burgundy">{value}</div>
            <div className="mt-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-charcoal/55">{label}</div>
        </div>
    );
}

function Bars({ title, data, testid }) {
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...entries.map(([, n]) => n));
    return (
        <div className="bg-cream-soft border border-burgundy/10 rounded-2xl p-6" data-testid={testid}>
            <h3 className="font-serif text-xl text-charcoal">{title}</h3>
            {entries.length === 0 ? (
                <p className="mt-3 text-sm text-charcoal/50">No data yet.</p>
            ) : (
                <ul className="mt-4 space-y-2.5">
                    {entries.map(([k, n]) => (
                        <li key={k} className="flex items-center gap-3 text-sm">
                            <span className="w-44 shrink-0 text-charcoal/75 truncate">{k}</span>
                            <span className="grow h-2 rounded-full bg-burgundy/10 overflow-hidden">
                                <span className="block h-full rounded-full bg-burgundy" style={{ width: `${(n / max) * 100}%` }} />
                            </span>
                            <span className="w-6 text-right font-medium">{n}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function AdminLeads() {
    const [authed, setAuthed] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [fService, setFService] = useState("");
    const [fStatus, setFStatus] = useState("");
    const [fDate, setFDate] = useState("");
    const [detail, setDetail] = useState(null);
    const [notesDraft, setNotesDraft] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/api/admin/me`, { withCredentials: true })
            .then(() => setAuthed(true))
            .catch(() => setAuthed(false));
    }, []);

    const loadLeads = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/admin/leads`, { withCredentials: true });
            setLeads(data.leads || []);
        } catch (e) {
            if (e?.response?.status === 401) setAuthed(false);
            else toast.error("Couldn't load leads.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (authed) loadLeads();
    }, [authed]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post(`${API}/api/admin/login`, { email, password }, { withCredentials: true });
            setAuthed(true);
            toast.success("Welcome back!");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/admin/logout`, {}, { withCredentials: true });
        } finally {
            setAuthed(false);
        }
    };

    const updateLead = async (id, patch, silent = false) => {
        try {
            await axios.patch(`${API}/api/admin/leads/${id}`, patch, { withCredentials: true });
            setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
            setDetail((d) => (d && d.id === id ? { ...d, ...patch } : d));
            if (!silent) toast.success("Lead updated");
        } catch {
            if (!silent) toast.error("Update failed — please try again.");
        }
    };

    const openDetail = (l) => {
        setDetail(l);
        setNotesDraft(l.notes || "");
        if (!l.read) updateLead(l.id, { read: true }, true);
    };

    const markAllRead = async () => {
        try {
            await axios.post(`${API}/api/admin/leads/mark-all-read`, {}, { withCredentials: true });
            setLeads((prev) => prev.map((l) => ({ ...l, read: true })));
            toast.success("All enquiries marked as read");
        } catch {
            toast.error("Couldn't mark as read.");
        }
    };

    const filtered = useMemo(
        () =>
            leads.filter((l) => {
                const q = search.trim().toLowerCase();
                if (
                    q &&
                    ![l.name, l.phone, l.email, l.business].some((v) =>
                        (v || "").toLowerCase().includes(q)
                    )
                )
                    return false;
                if (fService && l.service !== fService) return false;
                if (fStatus && l.status !== fStatus) return false;
                if (fDate && (l.created_at || "").slice(0, 10) !== fDate) return false;
                return true;
            }),
        [leads, search, fService, fStatus, fDate]
    );

    const exportCsv = () => {
        const cols = [
            "id", "created_at", "name", "business", "phone", "whatsapp_number", "email",
            "city", "service", "business_type", "specific_service", "timeline",
            "contact_method", "best_time", "project_description", "message", "source",
            "status", "notes", "completed", "read", "whatsapp_clicked", "callback_requested",
        ];
        const rows = filtered.map((l) =>
            cols.map((c) => `"${String(l[c] ?? "").replace(/"/g, '""')}"`).join(",")
        );
        const csv = [cols.join(","), ...rows].join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "analytrova-leads.csv";
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filtered.length} leads`);
    };

    if (authed === null) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center font-sans text-charcoal/60">
                Loading…
            </div>
        );
    }

    if (!authed) {
        return (
            <div className="min-h-screen bg-cream font-sans flex items-center justify-center p-5" data-testid="admin-login-page">
                <form
                    onSubmit={login}
                    className="w-full max-w-sm bg-cream-soft border border-burgundy/12 rounded-2xl p-8 shadow-[0_30px_60px_-40px_rgba(90,0,16,0.4)]"
                    data-testid="admin-login-form"
                >
                    <span className="w-12 h-12 rounded-xl bg-burgundy text-cream flex items-center justify-center font-serif text-xl">A</span>
                    <h1 className="mt-5 text-2xl text-charcoal">Admin Login</h1>
                    <p className="mt-1 text-sm text-charcoal/60">Analytrova leads dashboard — authorised team only.</p>
                    <div className="mt-6 space-y-4">
                        <div>
                            <label className="field-label" htmlFor="admin-email">Email</label>
                            <input id="admin-email" type="email" className="field" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
                        </div>
                        <div>
                            <label className="field-label" htmlFor="admin-password">Password</label>
                            <input id="admin-password" type="password" className="field" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
                        </div>
                        <button type="submit" className="btn btn-burgundy w-full justify-center" disabled={busy} data-testid="admin-login-submit">
                            {busy ? "Signing in…" : "Sign In"}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const total = leads.length;
    const newCount = leads.filter((l) => l.status === "New").length;
    const inProgress = leads.filter((l) => ["Contacted", "In Discussion", "Proposal Sent"].includes(l.status)).length;
    const converted = leads.filter((l) => l.status === "Converted").length;
    const unread = leads.filter((l) => !l.read).length;
    const byService = {};
    const bySource = {};
    leads.forEach((l) => {
        if (l.service) byService[l.service] = (byService[l.service] || 0) + 1;
        if (l.source) bySource[l.source] = (bySource[l.source] || 0) + 1;
    });
    const services = [...new Set(leads.map((l) => l.service).filter(Boolean))];

    return (
        <div className="min-h-screen bg-cream text-charcoal font-sans" data-testid="admin-dashboard">
            <header className="bg-burgundy text-cream">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-xl bg-cream text-burgundy flex items-center justify-center font-serif text-lg">A</span>
                        <div>
                            <div className="font-serif text-lg leading-tight">Analytrova</div>
                            <div className="text-[11px] tracking-[0.2em] uppercase text-cream/65">Leads Dashboard</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={loadLeads} className="btn btn-outline-cream !py-2 !px-4 text-xs" data-testid="admin-refresh">
                            <RefreshCw className="w-3.5 h-3.5" /> Refresh
                        </button>
                        <button onClick={exportCsv} className="btn btn-gold !py-2 !px-4 text-xs" data-testid="admin-export">
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                        <button onClick={logout} className="btn btn-outline-cream !py-2 !px-4 text-xs" data-testid="admin-logout">
                            <LogOut className="w-3.5 h-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                {unread > 0 && (
                    <div className="mb-6 flex flex-wrap items-center gap-3 bg-olive/20 border border-olive-deep/30 rounded-2xl px-5 py-4" data-testid="admin-unread-banner">
                        <Bell className="w-4 h-4 text-burgundy" />
                        <span className="text-sm text-charcoal/80">
                            <strong>{unread}</strong> new enquiry{unread > 1 ? "ies" : ""} waiting for review
                        </span>
                        <button onClick={markAllRead} className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-burgundy border border-burgundy/30 rounded-full px-4 py-1.5 hover:bg-burgundy hover:text-cream transition-colors" data-testid="admin-mark-all-read">
                            <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Stat label="Total Enquiries" value={total} testid="stat-total" />
                    <Stat label="New Leads" value={newCount} testid="stat-new" />
                    <Stat label="Leads in Progress" value={inProgress} testid="stat-progress" />
                    <Stat label="Converted Customers" value={converted} testid="stat-converted" />
                </div>

                <div className="grid lg:grid-cols-2 gap-4 mt-6">
                    <Bars title="Leads by Service" data={byService} testid="admin-by-service" />
                    <Bars title="Leads by Source" data={bySource} testid="admin-by-source" />
                </div>

                <div className="mt-8 flex flex-wrap gap-3 items-center">
                    <div className="relative grow min-w-[220px]">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40" />
                        <input
                            className="field !pl-10"
                            placeholder="Search name, phone or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="admin-search"
                        />
                    </div>
                    <select className="field !w-auto" value={fService} onChange={(e) => setFService(e.target.value)} data-testid="admin-filter-service">
                        <option value="">All services</option>
                        {services.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select className="field !w-auto" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="admin-filter-status">
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <input type="date" className="field !w-auto" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="admin-filter-date" />
                </div>

                <div className="mt-6 bg-cream-soft border border-burgundy/10 rounded-2xl overflow-x-auto">
                    <table className="w-full text-sm min-w-[960px]" data-testid="admin-leads-table">
                        <thead>
                            <tr className="text-left text-[11px] tracking-[0.16em] uppercase text-charcoal/50 border-b border-burgundy/10">
                                <th className="px-4 py-3">Lead ID</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Business</th>
                                <th className="px-4 py-3">Contact</th>
                                <th className="px-4 py-3">Service</th>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((l, i) => (
                                <tr
                                    key={l.id}
                                    className={`border-b border-burgundy/8 last:border-0 ${!l.read ? "bg-olive/15" : ""}`}
                                    data-testid={`admin-row-${i}`}
                                >
                                    <td className="px-4 py-3 text-charcoal/60 font-mono text-xs">
                                        {!l.read && <span className="inline-block w-2 h-2 rounded-full bg-burgundy mr-1.5" aria-label="unread" />}
                                        {(l.id || "").slice(0, 8)}
                                    </td>
                                    <td className="px-4 py-3 font-medium">
                                        {l.name || "—"}
                                        {l.source && <div className="text-[10px] tracking-wide uppercase text-charcoal/45">{l.source}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-charcoal/70">{l.business || "—"}</td>
                                    <td className="px-4 py-3 text-charcoal/70">
                                        <div>{l.phone || "—"}</div>
                                        {l.email && <div className="text-xs text-charcoal/50">{l.email}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-charcoal/70">{l.service || "—"}{l.specific_service ? <div className="text-xs text-charcoal/50">{l.specific_service}</div> : null}</td>
                                    <td className="px-4 py-3 text-charcoal/60 text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <select
                                            className="field !py-1.5 !px-2.5 text-xs !w-auto"
                                            value={l.status || "New"}
                                            onChange={(e) => updateLead(l.id, { status: e.target.value })}
                                            data-testid={`admin-status-${i}`}
                                        >
                                            {STATUSES.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => openDetail(l)}
                                            className="text-xs font-medium text-burgundy border-b border-burgundy/40 hover:border-burgundy pb-0.5"
                                            data-testid={`admin-view-${i}`}
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="px-4 py-12 text-center text-charcoal/50" data-testid="admin-empty">
                                        {loading ? "Loading leads…" : "No leads match your filters yet."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail modal */}
            {detail && (
                <div
                    className="fixed inset-0 z-[110] bg-burgundy/60 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setDetail(null)}
                    data-testid="admin-detail-overlay"
                >
                    <div
                        className="relative w-full max-w-2xl bg-cream-soft border border-gold-deep/40 rounded-2xl p-7 max-h-[90dvh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="admin-detail-modal"
                    >
                        <button
                            onClick={() => setDetail(null)}
                            aria-label="Close details"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full border border-burgundy/20 text-burgundy flex items-center justify-center hover:bg-burgundy hover:text-cream transition-colors"
                            data-testid="admin-detail-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <h3 className="font-serif text-2xl text-charcoal">{detail.name || "Unnamed lead"}</h3>
                        <p className="text-xs text-charcoal/50 mt-0.5">
                            Lead {(detail.id || "").slice(0, 8)} • {fmtDate(detail.created_at)}
                            {detail.source ? ` • via ${detail.source}` : ""}
                        </p>

                        <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                            {[
                                ["Business Name", detail.business],
                                ["Phone", detail.phone],
                                ["WhatsApp Number", detail.whatsapp_number],
                                ["Email", detail.email],
                                ["City / Location", detail.city],
                                ["Main Service", detail.service],
                                ["Specific Service", detail.specific_service],
                                ["Business Type", detail.business_type],
                                ["Timeline", detail.timeline],
                                ["Preferred Contact", detail.contact_method],
                                ["Best Time to Contact", detail.best_time],
                                ["Project Description", detail.project_description || detail.message],
                            ].map(([k, v]) => (
                                <div key={k} className="bg-cream/70 border border-burgundy/8 rounded-xl px-4 py-2.5">
                                    <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-charcoal/45">{k}</div>
                                    <div className="text-charcoal/85">{v || "—"}</div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2 text-xs">
                            <span className={`px-3 py-1 rounded-full ${detail.completed ? "bg-olive/30 text-charcoal" : "bg-gold/30 text-charcoal"}`} data-testid="admin-detail-completed">
                                {detail.completed ? "Questionnaire completed" : "Questionnaire incomplete"}
                            </span>
                            {detail.whatsapp_clicked && <span className="px-3 py-1 rounded-full bg-olive/30 text-charcoal">WhatsApp clicked</span>}
                            {detail.callback_requested && <span className="px-3 py-1 rounded-full bg-burgundy/10 text-burgundy font-medium">Callback requested</span>}
                        </div>

                        {(detail.transcript || []).length > 0 && (
                            <div className="mt-5">
                                <h4 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-charcoal/50">Questionnaire Answers</h4>
                                <ul className="mt-2 space-y-1.5 text-sm" data-testid="admin-detail-transcript">
                                    {detail.transcript.map((t, i) => (
                                        <li key={i} className="flex gap-2">
                                            <BarChart3 className="w-3.5 h-3.5 text-olive-deep shrink-0 mt-0.5" />
                                            <span className="text-charcoal/60">{t.q}</span>
                                            <span className="font-medium text-burgundy">{t.a}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {(detail.chat_history || []).length > 0 && (
                            <div className="mt-5">
                                <h4 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-charcoal/50">Chat History</h4>
                                <ul className="mt-2 space-y-1.5 text-sm max-h-44 overflow-y-auto" data-testid="admin-detail-chat">
                                    {detail.chat_history.map((m, i) => (
                                        <li key={i}>
                                            <span className="font-medium text-burgundy">{m.role === "user" ? "Customer" : "Olivia"}:</span>{" "}
                                            <span className="text-charcoal/70">{m.content}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mt-5">
                            <label className="field-label" htmlFor="admin-notes">Follow-up Notes</label>
                            <textarea
                                id="admin-notes"
                                rows="3"
                                className="field resize-none"
                                placeholder="Internal notes about this lead…"
                                value={notesDraft}
                                onChange={(e) => setNotesDraft(e.target.value)}
                                data-testid="admin-notes"
                            />
                            <div className="mt-3 flex items-center gap-3">
                                <button
                                    onClick={() => updateLead(detail.id, { notes: notesDraft })}
                                    className="btn btn-burgundy !py-2.5 !px-5 text-xs"
                                    data-testid="admin-notes-save"
                                >
                                    Save Notes
                                </button>
                                <div className="grow">
                                    <label className="sr-only" htmlFor="admin-detail-status">Status</label>
                                    <select
                                        id="admin-detail-status"
                                        className="field !py-2 text-xs"
                                        value={detail.status || "New"}
                                        onChange={(e) => updateLead(detail.id, { status: e.target.value })}
                                        data-testid="admin-detail-status"
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
